import React from 'react';
import { C } from '../../utils/styles';
import Icon from '../Icon';
import { brokerCommissionPct } from '../../utils/dashboardData';

function fmtMoney(n) {
  const v = Math.round(Number(n) || 0);
  if (Math.abs(v) >= 1000) return '$' + (v / 1000).toFixed(v >= 1_000_000 ? 2 : 1).replace(/\.0$/, '') + 'k';
  return '$' + v.toLocaleString('en-US');
}

function Avatar({ initials, color }) {
  return (
    <span style={{
      width: 32, height: 32, borderRadius: '50%',
      background: color || C.accent, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 800, flexShrink: 0,
      fontFamily: "'JetBrains Mono', monospace",
    }}>
      {initials}
    </span>
  );
}

const AVATAR_COLORS = [C.accent, C.green, C.purple, C.orange, '#0d9488', '#dc2626', '#475569'];

export default function CommissionSummary({ kpis, byAm = [], revenueSettings, isSuperUser = true, currentUserId, onAmClick }) {
  const allocation = revenueSettings?.spreadAllocation || [];
  const totalSpread = Number(kpis?.annualSpread || 0);
  const brokerPct = brokerCommissionPct(revenueSettings);

  const visibleByAm = isSuperUser
    ? byAm
    : byAm.filter((a) => a.id === currentUserId);

  return (
    <div
      data-testid="dashboard-commission"
      style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Icon name="cash-outline" style={{ fontSize: 18, color: C.purple }} />
        <h3 style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>
          Commission summary
        </h3>
      </div>

      {/* Spread allocation breakdown (super user only) */}
      {isSuperUser && (
        <div data-testid="commission-allocation" style={{ marginBottom: 18, padding: '12px 14px', background: C.bg, borderRadius: 10, border: `1px solid ${C.borderLight}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.textMid }}>Total annual spread</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(totalSpread)}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
            {allocation.map((row, idx) => {
              const pct = (Number(row?.percentage) || 0) / 100;
              const value = totalSpread * pct;
              return (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
                  <span style={{ color: C.textMid }}>{row?.category || 'Category'} <span style={{ color: C.textLight }}>({(pct * 100).toFixed(0)}%)</span></span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: C.text }}>{fmtMoney(value)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-AM table */}
      <div>
        <h4 style={{ fontSize: 12, fontWeight: 700, color: C.text, margin: '0 0 10px' }}>
          {isSuperUser ? 'Commission by account manager' : 'Your commission'}
        </h4>
        {visibleByAm.length === 0 ? (
          <div style={{ fontSize: 12, color: C.textLight, padding: '12px 0' }}>
            No commissionable clients in the current view.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {visibleByAm.map((a, idx) => (
              <button
                key={a.id}
                type="button"
                data-testid={`commission-am-${a.id}`}
                onClick={() => onAmClick && onAmClick(a)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '40px 1fr 100px 130px 130px',
                  gap: 12,
                  alignItems: 'center',
                  padding: '10px 8px',
                  border: 'none',
                  borderBottom: `1px solid ${C.borderLight}`,
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <Avatar initials={a.initials} color={AVATAR_COLORS[idx % AVATAR_COLORS.length]} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{a.name}</div>
                  <div style={{ fontSize: 11, color: C.textMid }}>
                    {a.count} client{a.count !== 1 ? 's' : ''}
                    {a.eligible ? ` · ${a.eligible} eligible` : ''}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: C.textMid, fontFamily: "'JetBrains Mono', monospace" }}>
                  {a.eligible || 0} lives
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: C.textLight }}>Spread</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(a.spread)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: C.textLight }}>Commission ({Math.round(brokerPct * 100)}%)</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.green, fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(a.commission)}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
